import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Download } from "lucide-react";
import { api, downloadFile } from "@/lib/api";
import { fmtMoney, fmtNum } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";

export default function Costing() {
  const { pid } = useParams();
  const [evm, setEvm] = useState(null);
  const [curve, setCurve] = useState([]);
  const [wbs, setWbs] = useState([]);

  useEffect(() => {
    api.get(`/projects/${pid}/evm`).then(({ data }) => setEvm(data));
    api.get(`/projects/${pid}/scurve`).then(({ data }) => setCurve(data));
    api.get(`/projects/${pid}/wbs`).then(({ data }) => setWbs(data.filter((a) => a.total_qty > 0)));
  }, [pid]);

  if (!evm) return <div className="text-sm text-muted-foreground">Computing earned value…</div>;

  return (
    <div data-testid="costing-page">
      <PageHead title="Dynamic Costing & Earned Value Management" subtitle="Financial metrics bound directly to physical site progress — no hidden overruns.">
        <Button variant="outline" onClick={() => downloadFile(`/reports/excel/${pid}`, "evm_pack.xlsx")} data-testid="costing-export-btn">
          <Download className="w-4 h-4 mr-1.5" /> Excel pack
        </Button>
      </PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-3">
        <Kpi label="BAC" value={fmtMoney(evm.bac)} testId="kpi-bac" />
        <Kpi label="Planned value (PV)" value={fmtMoney(evm.pv)} />
        <Kpi label="Earned value (EV)" value={fmtMoney(evm.ev)} tone="primary" testId="kpi-ev" />
        <Kpi label="Actual cost (AC)" value={fmtMoney(evm.ac)} testId="kpi-ac" />
        <Kpi label="Cost variance (CV)" value={fmtMoney(evm.cv)} tone={evm.cv >= 0 ? "good" : "bad"} />
        <Kpi label="Schedule variance (SV)" value={fmtMoney(evm.sv)} tone={evm.sv >= 0 ? "good" : "bad"} />
        <Kpi label="CPI" value={evm.cpi} tone={evm.cpi >= 1 ? "good" : "bad"} testId="kpi-cost-cpi" sub={evm.cpi >= 1 ? "Under budget" : "Over budget"} />
        <Kpi label="SPI" value={evm.spi} tone={evm.spi >= 0.95 ? "good" : "bad"} testId="kpi-cost-spi" sub={evm.spi >= 0.95 ? "On schedule" : "Behind schedule"} />
        <Kpi label="EAC forecast" value={fmtMoney(evm.eac)} />
        <Kpi label="ETC remaining" value={fmtMoney(evm.etc)} />
        <Kpi label="VAC" value={fmtMoney(evm.vac)} tone={evm.vac >= 0 ? "good" : "bad"} />
        <Kpi label="Idle-time cost leak" value={fmtMoney(evm.idle_cost)} tone="warn" />
      </div>

      <div className="grid xl:grid-cols-3 gap-3">
        <Panel title="Cumulative PV / EV / AC" className="xl:col-span-2" testId="evm-curve-panel">
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={curve} margin={{ top: 6, right: 10, left: 6 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(2, 7)} minTickGap={26} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1e7).toFixed(0)}Cr`} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="planned_value" name="Planned value" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted-foreground))" fillOpacity={0.12} strokeWidth={2} />
                <Area type="monotone" dataKey="actual_value" name="Earned value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.2} strokeWidth={2.5} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Cost composition" testId="cost-composition-panel">
          <div className="space-y-2">
            {[["Direct material / bought-out", evm.material_cost, "primary"], ["Direct labour", evm.labor_cost, "primary"],
              ["Equipment & machinery", evm.equipment_cost, "muted"],
              ["Indirect overheads (12%)", evm.indirect_cost, "muted"], ["Hindrance exposure", evm.hindrance_cost, "bad"],
              ["Idle-time leakage", evm.idle_cost, "warn"]].map(([l, v, tone]) => (
              <div key={l} className="flex items-center justify-between gap-3 border-b border-border pb-1.5 last:border-0">
                <span className="text-[13px] text-muted-foreground">{l}</span>
                <div className="flex items-center gap-2">
                  <Badge2 tone={tone}>{fmtNum((Number(v) / (evm.ac || 1)) * 100, 1)}%</Badge2>
                  <span className="num text-[13px] font-medium">{fmtMoney(v)}</span>
                </div>
              </div>
            ))}
            <div className="pt-2 text-[12px] text-muted-foreground">
              Manhours booked <span className="num text-foreground font-medium">{fmtNum(evm.manhours, 0)}</span> ·
              Physical completion <span className="num text-foreground font-medium">{evm.percent_complete}%</span>
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Activity-wise cost & earned value" className="mt-3" testId="cost-table-panel">
        <div className="overflow-x-auto max-h-[55vh]">
          <table className="w-full dense-table">
            <thead className="sticky-head"><tr><th>Code</th><th>Activity</th><th className="text-right">Rate</th><th className="text-right">Budget</th><th className="text-right">Executed</th><th className="text-right">Earned value</th><th className="text-right">Balance value</th><th className="text-right">Progress</th></tr></thead>
            <tbody>
              {wbs.map((a) => (
                <tr key={a.id} data-testid={`cost-row-${a.code}`}>
                  <td className="num text-primary">{a.code}</td>
                  <td className="max-w-[240px] truncate">{a.name}</td>
                  <td className="num text-right">{fmtNum(a.rate, 0)}</td>
                  <td className="num text-right">{fmtMoney(a.budget)}</td>
                  <td className="num text-right">{fmtNum(a.executed_qty, 0)} {a.unit}</td>
                  <td className="num text-right text-emerald-500">{fmtMoney(a.executed_qty * a.rate)}</td>
                  <td className="num text-right text-amber-500">{fmtMoney(a.balance_qty * a.rate)}</td>
                  <td className="num text-right">{a.progress_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
